// Admin management of the tester allowlist (allowlist/{lowercased-email} docs). The REAL
// access control is firestore.rules (only ken@futureengine.io's verified token may write);
// this lib is the data-access the /admin panel calls. Mirrors allowlist.ts's lowercase
// convention so ids match the rules' email.lower() gate.
import { collection, doc, getDocs, setDoc, deleteDoc } from "firebase/firestore";
import { db } from "./firebase";

/** The one admin. Hardcoded (mirrors the firestore.rules literal) — not env/config. */
export const ADMIN_EMAIL = "ken@futureengine.io";

export interface AllowlistEntry {
  email: string;
  addedAt: string | null;
  addedBy: string | null;
}

/** Basic shape check — enough to reject typos before a Firestore write. */
export function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

/** All allowlisted emails, sorted asc. Doc id IS the (lowercased) email. */
export async function listAllowlist(): Promise<AllowlistEntry[]> {
  const snap = await getDocs(collection(db, "allowlist"));
  return snap.docs
    .map((d) => {
      const data = d.data() as { addedAt?: string; addedBy?: string };
      return { email: d.id, addedAt: data.addedAt ?? null, addedBy: data.addedBy ?? null };
    })
    .sort((a, b) => a.email.localeCompare(b.email));
}

/** Add (or overwrite) an allowlist entry, stamping who/when. Throws on invalid email. */
export async function addToAllowlist(email: string): Promise<void> {
  const normalized = email.trim().toLowerCase();
  if (!isValidEmail(normalized)) throw new Error("Enter a valid email address.");
  await setDoc(doc(db, "allowlist", normalized), {
    addedAt: new Date().toISOString(),
    addedBy: ADMIN_EMAIL,
  });
}

/** Remove an allowlist entry. Refuses the admin's own email (self-lockout guard). */
export async function removeFromAllowlist(email: string): Promise<void> {
  const normalized = email.trim().toLowerCase();
  if (normalized === ADMIN_EMAIL) throw new Error("You can't remove yourself.");
  await deleteDoc(doc(db, "allowlist", normalized));
}
